export type EdgeIndicatorSide = 'top' | 'right' | 'bottom' | 'left';

export type EdgeIndicatorPosition = {
  angle: number;
  side: EdgeIndicatorSide;
  x: number;
  y: number;
};

export type ViewportInsets = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

type Point = { x: number; y: number };
type ViewportSize = { width: number; height: number };

export function projectOffscreenTarget(
  target: Point,
  viewport: ViewportSize,
  insets: ViewportInsets,
  targetRadius: number,
): EdgeIndicatorPosition | null {
  const targetOverlapsViewport = target.x + targetRadius >= 0
    && target.x - targetRadius <= viewport.width
    && target.y + targetRadius >= 0
    && target.y - targetRadius <= viewport.height;
  if (targetOverlapsViewport) return null;

  const left = insets.left;
  const right = Math.max(left, viewport.width - insets.right);
  const top = insets.top;
  const bottom = Math.max(top, viewport.height - insets.bottom);
  const center = { x: (left + right) / 2, y: (top + bottom) / 2 };
  const dx = target.x - center.x;
  const dy = target.y - center.y;
  if (dx === 0 && dy === 0) return null;

  const horizontalDistance = dx > 0 ? right - center.x : center.x - left;
  const verticalDistance = dy > 0 ? bottom - center.y : center.y - top;
  const horizontalScale = Math.abs(dx) > .001 ? horizontalDistance / Math.abs(dx) : Infinity;
  const verticalScale = Math.abs(dy) > .001 ? verticalDistance / Math.abs(dy) : Infinity;
  const scale = Math.min(horizontalScale, verticalScale);
  const hitsVerticalEdge = horizontalScale <= verticalScale;

  return {
    angle: Math.atan2(dy, dx) * 180 / Math.PI,
    side: hitsVerticalEdge
      ? dx > 0 ? 'right' : 'left'
      : dy > 0 ? 'bottom' : 'top',
    x: Math.max(left, Math.min(right, center.x + dx * scale)),
    y: Math.max(top, Math.min(bottom, center.y + dy * scale)),
  };
}

export function spreadEdgeIndicators(
  positions: EdgeIndicatorPosition[],
  viewport: ViewportSize,
  insets: ViewportInsets,
  minimumGap: number,
): EdgeIndicatorPosition[] {
  const result = positions.map((position) => ({ ...position }));
  const sides: EdgeIndicatorSide[] = ['top', 'right', 'bottom', 'left'];

  for (const side of sides) {
    const vertical = side === 'left' || side === 'right';
    const minimum = vertical ? insets.top : insets.left;
    const maximum = vertical
      ? viewport.height - insets.bottom
      : viewport.width - insets.right;
    const members = result
      .filter((position) => position.side === side)
      .sort((left, right) => (vertical ? left.y - right.y : left.x - right.x));
    if (members.length < 2) continue;

    const availableGap = members.length > 1
      ? Math.min(minimumGap, (maximum - minimum) / (members.length - 1))
      : minimumGap;
    let cursor = minimum;
    for (const member of members) {
      const coordinate = vertical ? member.y : member.x;
      const next = Math.max(coordinate, cursor);
      if (vertical) member.y = next;
      else member.x = next;
      cursor = next + availableGap;
    }

    const last = members.at(-1)!;
    const overflow = (vertical ? last.y : last.x) - maximum;
    if (overflow > 0) {
      for (const member of members) {
        if (vertical) member.y -= overflow;
        else member.x -= overflow;
      }
    }
  }

  return result;
}
